import type {
  AmenityPackId,
  ScoreConfig,
  ScoreMode,
  SearchFilters,
  ResolvedScoreBand,
} from "./types";

export interface AmenityPackSectionDefinition {
  id: string;
  label: string;
  weight: number;
  types: string[];
}

export interface AmenityPackDefinition {
  id: Exclude<AmenityPackId, "custom">;
  label: string;
  description: string;
  scoreMode: ScoreMode;
  defaultTypes: string[];
  defaultBandFractions: [number, number, number];
  defaultBandWeights: [number, number, number];
  sections: AmenityPackSectionDefinition[];
}

const MIN_BAND_GAP = 0.05;
const MIN_BAND_WEIGHT = 0.01;

export const DEFAULT_AMENITY_PACK_ID: AmenityPackId = "urban-demand";
export const CUSTOM_ADDITIONS_SECTION_WEIGHT = 0.15;
export const MAX_RECOMMENDED_SELECTED_TYPES = 8;

export const amenityPacks: AmenityPackDefinition[] = [
  {
    id: "urban-demand",
    label: "Urban demand",
    description: "Walkable daily needs, transit, and lifestyle amenities that support renter demand.",
    scoreMode: "fit",
    defaultTypes: ["grocery_store", "pharmacy", "transit_station", "cafe", "restaurant", "gym", "park"],
    defaultBandFractions: [0.25, 0.5, 1],
    defaultBandWeights: [0.55, 0.3, 0.15],
    sections: [
      { id: "daily-convenience", label: "Daily convenience", weight: 0.3, types: ["grocery_store", "pharmacy"] },
      { id: "mobility", label: "Mobility", weight: 0.25, types: ["transit_station"] },
      { id: "lifestyle", label: "Lifestyle", weight: 0.3, types: ["cafe", "restaurant", "gym"] },
      { id: "green-space", label: "Green space", weight: 0.15, types: ["park"] },
    ],
  },
  {
    id: "neighborhood-essentials",
    label: "Neighborhood essentials",
    description: "Errands, healthcare, and community amenities that make everyday living easier.",
    scoreMode: "fit",
    defaultTypes: ["grocery_store", "pharmacy", "doctor", "school", "park"],
    defaultBandFractions: [0.33, 0.66, 1],
    defaultBandWeights: [0.45, 0.35, 0.2],
    sections: [
      { id: "errands", label: "Errands", weight: 0.45, types: ["grocery_store", "pharmacy"] },
      { id: "everyday-services", label: "Everyday services", weight: 0.25, types: ["doctor"] },
      { id: "community", label: "Community", weight: 0.3, types: ["school", "park"] },
    ],
  },
  {
    id: "family-friendly",
    label: "Family-friendly",
    description: "Schools, parks, and day-to-day support amenities that strengthen family appeal.",
    scoreMode: "fit",
    defaultTypes: ["grocery_store", "pharmacy", "preschool", "school", "park"],
    defaultBandFractions: [0.4, 0.7, 1],
    defaultBandWeights: [0.4, 0.35, 0.25],
    sections: [
      { id: "family-infrastructure", label: "Family infrastructure", weight: 0.45, types: ["preschool", "school"] },
      { id: "daily-needs", label: "Daily needs", weight: 0.3, types: ["grocery_store", "pharmacy"] },
      { id: "outdoor-space", label: "Outdoor space", weight: 0.25, types: ["park"] },
    ],
  },
  {
    id: "lifestyle-pull",
    label: "Lifestyle pull",
    description: "Food, fitness, retail, and leisure amenities that add premium renter appeal.",
    scoreMode: "fit",
    defaultTypes: ["cafe", "restaurant", "gym", "shopping_mall", "park"],
    defaultBandFractions: [0.25, 0.5, 1],
    defaultBandWeights: [0.55, 0.3, 0.15],
    sections: [
      { id: "food-social", label: "Food and social", weight: 0.45, types: ["cafe", "restaurant"] },
      { id: "wellness-retail", label: "Wellness and retail", weight: 0.35, types: ["gym", "shopping_mall"] },
      { id: "open-space", label: "Open space", weight: 0.2, types: ["park"] },
    ],
  },
  {
    id: "coverage-gaps",
    label: "Coverage gaps",
    description: "A diagnostic lens for thin nearby coverage in essential daily amenities and transit.",
    scoreMode: "gap",
    defaultTypes: ["grocery_store", "pharmacy", "park", "transit_station"],
    defaultBandFractions: [0.35, 0.7, 1],
    defaultBandWeights: [0.4, 0.35, 0.25],
    sections: [
      { id: "convenience-gaps", label: "Convenience gaps", weight: 0.5, types: ["grocery_store", "pharmacy"] },
      { id: "mobility-gaps", label: "Mobility gaps", weight: 0.25, types: ["transit_station"] },
      { id: "green-space-gaps", label: "Green space gaps", weight: 0.25, types: ["park"] },
    ],
  },
];

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function normalizeWeights(
  weights: [number, number, number],
): [number, number, number] {
  const safe = weights.map((weight) =>
    Number.isFinite(weight) ? Math.max(weight, MIN_BAND_WEIGHT) : MIN_BAND_WEIGHT,
  ) as [number, number, number];
  const total = safe.reduce((sum, weight) => sum + weight, 0) || 1;

  return safe.map((weight) => Number((weight / total).toFixed(4))) as [number, number, number];
}

export function getAmenityPack(packId: AmenityPackId): AmenityPackDefinition | null {
  if (packId === "custom") {
    return null;
  }

  return amenityPacks.find((pack) => pack.id === packId) ?? amenityPacks[0];
}

export function createDefaultScoreConfig(packId: AmenityPackId): ScoreConfig {
  const pack = getAmenityPack(packId) ?? getAmenityPack(DEFAULT_AMENITY_PACK_ID);

  return {
    bands: [
      { fraction: pack?.defaultBandFractions[0] ?? 0.25, weight: pack?.defaultBandWeights[0] ?? 0.55 },
      { fraction: pack?.defaultBandFractions[1] ?? 0.5, weight: pack?.defaultBandWeights[1] ?? 0.3 },
      { fraction: 1, weight: pack?.defaultBandWeights[2] ?? 0.15 },
    ],
  };
}

export function normalizeScoreConfig(input: ScoreConfig, fallbackPackId: AmenityPackId): ScoreConfig {
  const fallback = createDefaultScoreConfig(fallbackPackId);
  const sourceBands = input.bands?.length === 3 ? input.bands : fallback.bands;
  const firstFraction = clamp(sourceBands[0]?.fraction ?? fallback.bands[0].fraction, MIN_BAND_GAP, 1 - MIN_BAND_GAP * 2);
  const secondFraction = clamp(
    sourceBands[1]?.fraction ?? fallback.bands[1].fraction,
    firstFraction + MIN_BAND_GAP,
    1 - MIN_BAND_GAP,
  );
  const normalizedWeights = normalizeWeights([
    sourceBands[0]?.weight ?? fallback.bands[0].weight,
    sourceBands[1]?.weight ?? fallback.bands[1].weight,
    sourceBands[2]?.weight ?? fallback.bands[2].weight,
  ]);

  return {
    bands: [
      { fraction: Number(firstFraction.toFixed(2)), weight: normalizedWeights[0] },
      { fraction: Number(secondFraction.toFixed(2)), weight: normalizedWeights[1] },
      { fraction: 1, weight: normalizedWeights[2] },
    ],
  };
}

export function resolveScoreBands(radiusMeters: number, scoreConfig: ScoreConfig): ResolvedScoreBand[] {
  const normalized = normalizeScoreConfig(scoreConfig, DEFAULT_AMENITY_PACK_ID);

  return normalized.bands.map((band) => ({
    fraction: band.fraction,
    weight: band.weight,
    radiusMeters: Math.max(1, Math.round(radiusMeters * band.fraction)),
  }));
}

export function getDefaultPackTypes(packId: AmenityPackId): string[] {
  const pack = getAmenityPack(packId);

  return pack ? [...pack.defaultTypes] : [];
}

export function createPackDefaultFilters(packId: AmenityPackId): SearchFilters {
  return {
    includedTypes: getDefaultPackTypes(packId),
    operatingStatus: ["OPERATING_STATUS_OPERATIONAL"],
  };
}

export function areTypeSetsEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const rightSet = new Set(right);

  return unique(left).every((item) => rightSet.has(item));
}

export function areScoreConfigsEqual(left: ScoreConfig, right: ScoreConfig): boolean {
  const normalizedLeft = normalizeScoreConfig(left, DEFAULT_AMENITY_PACK_ID);
  const normalizedRight = normalizeScoreConfig(right, DEFAULT_AMENITY_PACK_ID);

  return normalizedLeft.bands.every(
    (band, index) =>
      band.fraction === normalizedRight.bands[index]?.fraction &&
      band.weight === normalizedRight.bands[index]?.weight,
  );
}

export function isPackCustomized(
  packId: AmenityPackId,
  selectedTypes: string[],
  scoreConfig: ScoreConfig,
): boolean {
  if (packId === "custom") {
    return true;
  }

  return (
    !areTypeSetsEqual(selectedTypes, getDefaultPackTypes(packId)) ||
    !areScoreConfigsEqual(scoreConfig, createDefaultScoreConfig(packId))
  );
}
